import { Badge } from "@/components/ui/badge";
import { useLingui } from "@lingui/react/macro";
import { cn } from "@/lib/utils";
import type { MenuItemRow } from "./types";

interface MenuItemTileProps {
  item: MenuItemRow;
  onClick?: () => void;
}

function MetaCol({ label, value }: { label: string; value?: string }) {
  return (
    <div className="flex w-24 shrink-0 flex-col gap-2 sm:w-40">
      <span className="text-sm text-(--color-text-secondary)">{label}</span>
      <span className="text-sm font-medium text-(--color-text-primary)">{value || "—"}</span>
    </div>
  );
}

export function MenuItemTile({ item, onClick }: MenuItemTileProps) {
  const { t } = useLingui();
  // The row's `unitCost` carries the menu item's selling price.
  const price = item.unitCost != null ? `$${item.unitCost.toFixed(2)}` : undefined;

  return (
    <div
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={onClick ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}
      className={cn(
        "flex items-center justify-between gap-3 rounded-[0.625rem] border border-(--color-border) bg-(--color-surface-muted) p-3 transition-colors",
        onClick && "cursor-pointer hover:bg-(--color-surface-muted)/60",
      )}
    >
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <div className="flex size-17 shrink-0 items-center justify-center overflow-hidden rounded-[0.625rem] border border-(--shell-border) bg-(--color-surface-content)">
          {item.imageUrl ? (
            <img src={item.imageUrl} alt={item.name} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-xs text-(--color-text-secondary)">
              {t`IMG`}
            </div>
          )}
        </div>

        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-base font-medium text-(--color-text-primary)">{item.name}</span>
            {item.status === "Published" && (
              <Badge className="shrink-0 rounded-full bg-(--tag-blue) px-2 py-0.5 text-xs font-medium text-white hover:bg-(--tag-blue)/90">
                {t`Published`}
              </Badge>
            )}
            {item.status === "Draft" && (
              <Badge variant="secondary" className="shrink-0 rounded-full px-2 py-0.5 text-xs font-medium">
                {t`Draft`}
              </Badge>
            )}
          </div>
          {item.recipeCode && (
            <span className="truncate text-sm font-medium text-(--color-text-secondary)">
              {item.recipeCode.toUpperCase()}
            </span>
          )}
        </div>
      </div>

      <MetaCol label={t`Category:`} value={item.category} />
      <MetaCol label={t`Price:`} value={price} />
    </div>
  );
}
